<template>
  <div ref="container" v-on:click="closeCalendar" class="container">
    <div
      v-for="(insertionOrderBudgetListItem,
      index) in form.insertionOrderBudgetList"
      :key="insertionOrderBudgetListItem.id"
    >
      <div class="form-container">
        <BudgetPeriodPicker
          :start="insertionOrderBudgetListItem.startTime"
          :end="insertionOrderBudgetListItem.endTime"
          :dummyStartDateForInitialMonth="dummyStartDateForInitialMonth"
          :dummyEndDateForInitialMonth="dummyEndDateForInitialMonth"
          :disabled="budget.latestRequest.requestStatus === 'waiting'"
          :disabled-already-start="!budget.isNew"
          :usable-start-date-filter="usableStartDateFilter"
          :usable-end-date-filter="usableEndDateFilter"
          :occupiedPeriods="form.getOccupiedPeriods(index, budget.id)"
          :changeIsOpenCalendar="type => changeIsOpenCalendar(type, index)"
          :isOpenStartCalendar="openedStartCalendarIndex === index"
          :isOpenEndCalendar="openedEndCalendarIndex === index"
          :isNew="budget.isNew"
          @inputStartDate="date => onInputStartDate(index, date)"
          @inputEndDate="date => onInputEndDate(index, date)"
        />
        <div class="budget-currency">
          <v-text-field
            :value="
              getDisplayBudget(insertionOrderBudgetListItem.budget, index)
            "
            v-on:input="value => onInputBudget(index, value)"
            v-on:focus="() => (focusedBudgetIndex = index)"
            v-on:blur="
              () => {
                if (insertionOrderBudgetListItem.budget) {
                  insertionOrderBudgetListItem.budget = insertionOrderBudgetListItem.budget.replace(
                    /[^0-9]/g,
                    '',
                  )
                }
                focusedBudgetIndex = null
              }
            "
            @keydown.arrow-down.prevent
            @keydown.arrow-up.prevent
            @wheel.prevent
            :disabled="budget.latestRequest.requestStatus === 'waiting'"
            label="予算（JPY）"
            placeholder="500,000"
            type=""
          />
        </div>
        <button
          @click="() => removeBudget(index)"
          :disabled="form.insertionOrderBudgetList.length === 1"
          class="button-delete"
        >
          <v-icon
            :color="
              form.insertionOrderBudgetList.length === 1 ? '#CCC' : '#999'
            "
            >delete</v-icon
          >
        </button>
      </div>
      <div>
        <div
          v-if="
            showValidationError &&
              index in validationErrors &&
              validationErrors[index].length > 0
          "
          v-for="error in validationErrors[index]"
          class="validation-error"
        >
          {{ error }}
        </div>
      </div>
    </div>
    <v-btn
      @click.native="addBudget"
      :disabled="
        !isValid ||
          insertionOrder.budgetList.length +
            form.insertionOrderBudgetList.length >=
            52
      "
      color="primary"
      class="button-add-budget"
    >
      予算を追加する
    </v-btn>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { CreateBudgetRequestForm } from '@/models/BudgetRequest'
import {
  actions as snackbarActions,
  snackbarType,
} from '@/vuex/modules/ui/snackbar/constants'
import BudgetPeriodPicker from './BudgetPeriodPicker.vue'
import { dummyStartDateForInitialMonth } from '@/components/Advertiser/Operation/IO/Service/getDummyStartDateForInitialMonthService'

export default {
  name: 'EditBudgetForm',
  components: { BudgetPeriodPicker },
  props: {
    insertionOrder: {
      type: Object,
      require: false,
      default: () => {},
    },
    currency: {
      type: String,
      require: false,
      default: 'JPY',
    },
    budget: {
      type: Object,
      require: false,
      default: () => {},
    },
    budgetList: {
      type: Array,
      required: true,
    },
    showValidationError: {
      type: Boolean,
      default: false,
    },
    onChangeShowValidationError: {
      type: Function,
      default: () => {},
    },
    onValidate: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      historyVisible: false,
      focusedBudgetIndex: null,
      openedStartCalendarIndex: undefined,
      openedEndCalendarIndex: undefined,
      validationErrors: [],
      isValid: false,
      form: new CreateBudgetRequestForm(this.insertionOrder, [this.budget]),
      // date-pickerにデフォルトで表示される月を指定する機能がないため、それを実現するために一瞬だけ表示するダミーの日付
      dummyStartDateForInitialMonth: undefined,
      dummyEndDateForInitialMonth: undefined,
    }
  },
  computed: {
    confirmationBody() {
      const currency = this.currency
      const { formatCurrency } = this.$options.filters
      // eslint-disable-next-line
      return `
      <div style="font-size: 0.9rem; margin-top: -50px;">
        <div>
          <table>
            <thead>
              <tr style="font-weight: 400;">
                <th style="width: 75px; text-align: center;">開始日</th>
                <th style="width: 10px;"></th>
                <th style="width: 75px; text-align: center;">終了日</th>
                <th style="width: 75px; text-align: right;">予算</th>
              </tr>
            </thead>
            <tbody>
                ${this.form.insertionOrderBudgetList.reduce(
                  (tdList, insertionOrderBudgetListItem) =>
                    tdList +
                    `
                    <tr>
                      <td style="padding-top: 5px; text-align: center;">${dayjs(
                        insertionOrderBudgetListItem.startTime,
                      ).format('YYYY/M/D')}
                      </td>
                      <td style="padding-top: 5px; text-align: center;">〜</td>
                      <td style="padding-top: 5px; text-align: center;">${dayjs(
                        insertionOrderBudgetListItem.endTime,
                      ).format('YYYY/M/D')}
                      </td>
                      <td style="text-align: right; padding-top: 5px;">${formatCurrency(
                        insertionOrderBudgetListItem.budget,
                        currency,
                      )}
                      </td>
                    </tr>
                    `,
                  '',
                )}
            </tbody>
          </table>
        </div>
        案件予算のご申請をいただく場合、当社の定める利用規約にご同意の上、
        保存ボタンを押して予算申請をお願いします。
        <a href="https://www.geologic.co.jp/tos" target="_blank">利用規約を確認する</a>
        </div>
        `
    },
    confirmationMessage() {
      if (this.insertionOrder.isNew) {
        return '案件登録時に申請されます'
      } else {
        return '案件予算を申請します'
      }
    },
    expandIconName() {
      return this.historyVisible ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
    },
    /**
     * 既に申請予定、申請中、承認済みの予算が持つ期間かどうか判定するメソッドを返す
     */
    usableStartDateFilter() {
      // 新規案件の場合、requestStatusがない
      if (this.insertionOrder.isNew) {
        return date =>
          this.budgetList.every(
            budget =>
              dayjs(budget.summary.startTime).isAfter(date) ||
              dayjs(budget.summary.endTime).isBefore(date),
          )
      }
      return date =>
        this.budgetList
          .filter(budget =>
            ['new', 'waiting', 'approved'].includes(budget.requestStatus),
          )
          .every(
            budget =>
              dayjs(budget.summary.startTime).isAfter(date) ||
              dayjs(budget.summary.endTime).isBefore(date),
          )
    },
  },
  watch: {
    isValid(validity) {
      this.$emit('validate', validity)
    },
    form: {
      deep: true,
      handler() {
        this.validate()
      },
    },
    showValidationError() {
      this.validate()
    },
  },
  methods: {
    addBudget() {
      this.form.addBudget()
      this.validate()
    },
    removeBudget(removeIndex) {
      this.form.removeBudget(removeIndex)
      this.validate()
    },
    validate() {
      this.isValid = this.form.isValid
      this.$emit('onValidate', this.form.isValid)
      this.validationErrors = this.form.validationErrors
    },
    onInputStartDate(index, date) {
      if (date === undefined) {
        this.form.insertionOrderBudgetList[index].startTime = undefined
        return
      }
      this.form.insertionOrderBudgetList[index].startTime = `${date} 00:00:00`
      this.validate()
    },
    onInputEndDate(index, date) {
      if (date === undefined) {
        this.form.insertionOrderBudgetList[index].endTime = undefined
        return
      }
      this.form.insertionOrderBudgetList[index].endTime = `${date} 23:59:59`
      this.validate()
    },
    onInputBudget(index, value) {
      this.form.insertionOrderBudgetList[index].budget = value.replaceAll(
        ',',
        '',
      )
      this.validate()
    },
    usableEndDateFilter(date, startDate) {
      var filteredBudgetList = []
      // 新規案件の場合、requestStatusがない
      if (this.insertionOrder.isNew) {
        filteredBudgetList = this.budgetList.filter(budget =>
          dayjs(budget.summary.startTime).isAfter(startDate),
        )
        if (!filteredBudgetList.length) {
          return true
        }
      } else {
        filteredBudgetList = this.budgetList.filter(
          budget =>
            ['new', 'waiting', 'approved'].includes(budget.requestStatus) &&
            dayjs(budget.summary.startTime).isAfter(startDate),
        )
        if (!filteredBudgetList.length) {
          return true
        }
      }
      return filteredBudgetList.every(budget =>
        dayjs(budget.summary.startTime).isAfter(date),
      )
    },
    async submit() {
      this.onChangeShowValidationError(true)
      if (!this.form.isValid) {
        this.onChangeShowValidationError(true)
        return
      }
      const result = await this.$confirm(this.confirmationMessage, {
        body: this.confirmationBody,
        cancelText: 'キャンセル',
        okText: '保存',
      })
      if (!result) {
        return
      }
      if (this.insertionOrder.isNew) {
        // 新規予算申請用IDをセットしておく
        const budgetList = this.form.toBudgetList()
        this.$emit('addBudget', budgetList)
      } else {
        // 既存案件の予算ならこう
        await this.form.submit()
        this.insertionOrder.fetchBudgetList()
        this.$store.dispatch(snackbarActions.SHOW_SNACKBAR, {
          type: snackbarType.SUCCESS,
          message: '申請しました',
        })
      }
      this.$emit('success')
    },
    getDisplayBudget(budget, index) {
      if (this.focusedBudgetIndex === index) {
        return budget
      }
      if (typeof budget === 'number') return budget.toLocaleString()
      const parsedBudget = parseInt(
        budget.replace(/[Ａ-Ｚａ-ｚ０-９]/g, s =>
          String.fromCharCode(s.charCodeAt(0) - 65248),
        ),
      )
      return isNaN(parsedBudget) ? budget : parsedBudget.toLocaleString()
    },
    changeIsOpenCalendar(type, index) {
      const BUDGET_ITEM_HEIGHT = 68
      const CALENDAR_HEIGHT = 280
      const FIXED_HEIGHT = 320
      // カレンダーが展開した際に隠れてしまう量を計算し、その分スクロールする
      const scroll_offset =
        FIXED_HEIGHT +
        CALENDAR_HEIGHT +
        BUDGET_ITEM_HEIGHT * (index + 1) -
        window.innerHeight
      if (scroll_offset > 0) {
        setTimeout(() => this.$refs.container.scrollTo(0, scroll_offset), 10)
      }

      if (type === 'end') {
        if (this.openedEndCalendarIndex === index) {
          this.openedEndCalendarIndex = undefined
          this.openedStartCalendarIndex = undefined
          return
        }

        this.dummyEndDateForInitialMonth = this.form.insertionOrderBudgetList[
          index
        ].startTime
        this.openedEndCalendarIndex = index
        setTimeout(() => (this.dummyEndDateForInitialMonth = undefined), 1)
        this.openedStartCalendarIndex = undefined
        return
      }

      if (type === 'start') {
        // 現在開かれているカレンダーと同じカレンダーを開いた時
        if (this.openedStartCalendarIndex === index) {
          this.openedStartCalendarIndex = undefined
          return
        }

        // 現在開かれているカレンダーと異なるカレンダーを開いた時、いつのカレンダーを開くかを規定
        this.dummyStartDateForInitialMonth = dummyStartDateForInitialMonth({
          budgetList: this.budgetList,
          formBudgetList: this.form.insertionOrderBudgetList,
        })
        this.openedStartCalendarIndex = index
      }
    },
    closeCalendar() {
      // FIXME: カレンダーの日付を選択した時にstopPropagationする方法が見つからないので保留
      this.openedStartCalendarIndex = undefined
      this.openedEndCalendarIndex = undefined
    },
  },
}
</script>

<style scoped lang="scss">
.container {
  overflow-y: scroll;
  max-height: 70vh;
}

.expandButton {
  width: 100%;
  margin: 0;
  padding: 0;

  ::v-deep .v-btn__content {
    justify-content: flex-start;
  }
}

.budget-title {
  padding-right: 30px;
}

.budget-currency {
  width: 50%;
  margin-left: 30px;
}

.form-container {
  display: flex;
  align-items: flex-start;
  margin-right: 30px;
  padding-bottom: 0;
  overflow-y: hidden;
}

.validation-error {
  color: #ff0000;
  padding-left: 40px;
}

.button-add-budget {
  margin-top: 20px;
}

.button-delete {
  margin-top: 20px;
  margin-left: 4px;
}
</style>
